(function () {
    'use strict';

    const MARKET_CONTEXT = window.WusthofMarketContext || {};
    const STOREFRONT_COUNTRY_CODE = String(MARKET_CONTEXT.countryCode || 'DE').toUpperCase();
    const BARCODE_CACHE_KEY = 'wusthof_barcode_index_v4_' + STOREFRONT_COUNTRY_CODE;
    const BARCODE_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 Stunden
    const STOREFRONT_API_TOKEN = 'fd51b55cf209a8699eceecd5fc8981a4';
    const GRAPHQL_URL = 'https://wusthof-wholesale-germany.myshopify.com/api/2026-07/graphql.json';
    const CLOUDMERSIVE_API_KEY = '47f521d2-6c0e-4d91-a4df-7e3af20a14d0';
    const IMAGE_MAX_DIMENSION = 1600;
    const IMAGE_JPEG_QUALITY = 0.86;

    let barcodeIndex = null;
    let barcodeIndexPromise = null;
    let barcodeIndexLoadedFromCache = false;

    document.addEventListener('DOMContentLoaded', function () {
        const barcodeInput = document.getElementById('barcodeInput');
        const startScanner = document.getElementById('startScanner');

        if (!barcodeInput || !startScanner) {
            return;
        }

        // Sofort im Hintergrund starten. In vielen Fällen ist die EAN-Zuordnung
        // fertig, bevor der Kunde das Foto aufgenommen und hochgeladen hat.
        preloadBarcodeIndex();

        startScanner.addEventListener('click', function () {
            barcodeInput.value = '';
            setTimeout(function () {
                barcodeInput.click();
            }, 100);
        });

        barcodeInput.addEventListener('change', async function (event) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;

            try {
                const processedFile = await processImageForIOS(file);
                await scanBarcodeFromImage(processedFile);
            } catch (error) {
                console.error('Barcode-Bildverarbeitung fehlgeschlagen:', error);
                alert('Bild konnte nicht verarbeitet werden. Bitte versuche ein anderes Foto.');
            } finally {
                event.target.value = '';
            }
        });
    });

    function normalizeBarcode(value) {
        const raw = String(value || '').trim();
        if (!raw) return '';

        // Barcode-APIs liefern EAN/UPC je nach Bild gelegentlich mit Leerzeichen,
        // Bindestrichen oder Zeilenumbrüchen zurück. Für numerische GTINs werden
        // deshalb ausschließlich die Ziffern für den Abgleich verwendet.
        const digits = raw.replace(/\D/g, '');
        if ([8, 12, 13, 14].indexOf(digits.length) !== -1) {
            return digits;
        }

        // Fallback für nicht rein numerische Codes.
        return raw.replace(/\s+/g, '');
    }

    function barcodeCandidates(value) {
        const normalized = normalizeBarcode(value);
        if (!normalized) return [];

        const candidates = [normalized];

        // UPC-A und EAN-13 mit führender Null repräsentieren denselben GTIN.
        // Manche Scanner geben 12, andere 13 Stellen zurück.
        if (/^\d{12}$/.test(normalized)) {
            candidates.push('0' + normalized);
        } else if (/^0\d{12}$/.test(normalized)) {
            candidates.push(normalized.slice(1));
        }

        return candidates.filter(function (candidate, index, list) {
            return list.indexOf(candidate) === index;
        });
    }

    function addBarcodeMapping(items, barcode, variant) {
        if (!variant || !variant.sku || !variant.id) return;

        barcodeCandidates(barcode).forEach(function (candidate) {
            if (!items[candidate]) {
                items[candidate] = {
                    s: variant.sku,
                    v: numericVariantId(variant.id)
                };
            }
        });
    }

    function numericVariantId(globalId) {
        if (!globalId) return '';
        return String(globalId).replace('gid://shopify/ProductVariant/', '');
    }

    function readBarcodeCache() {
        try {
            const raw = localStorage.getItem(BARCODE_CACHE_KEY);
            if (!raw) return null;

            const parsed = JSON.parse(raw);
            if (!parsed || !parsed.createdAt || !parsed.items) return null;

            if ((Date.now() - parsed.createdAt) > BARCODE_CACHE_TTL_MS) {
                localStorage.removeItem(BARCODE_CACHE_KEY);
                return null;
            }

            return parsed.items;
        } catch (error) {
            console.warn('Barcode-Cache konnte nicht gelesen werden:', error);
            return null;
        }
    }

    function writeBarcodeCache(items) {
        try {
            localStorage.setItem(BARCODE_CACHE_KEY, JSON.stringify({
                createdAt: Date.now(),
                items: items
            }));
        } catch (error) {
            // Safari Private Mode / voller Speicher: Der Scanner funktioniert
            // trotzdem weiter; lediglich der persistente Cache fällt weg.
            console.warn('Barcode-Cache konnte nicht gespeichert werden:', error);
        }
    }

    async function preloadBarcodeIndex() {
        try {
            await loadBarcodeIndex(false);
        } catch (error) {
            console.warn('Barcode-Zuordnung konnte nicht vorgeladen werden:', error);
        }
    }

    async function loadBarcodeIndex(forceRefresh) {
        if (!forceRefresh && barcodeIndex) {
            return barcodeIndex;
        }

        if (!forceRefresh) {
            const cached = readBarcodeCache();
            if (cached) {
                barcodeIndex = cached;
                barcodeIndexLoadedFromCache = true;
                return barcodeIndex;
            }
        }

        if (barcodeIndexPromise && !forceRefresh) {
            return barcodeIndexPromise;
        }

        barcodeIndexPromise = fetchCompleteBarcodeIndex()
            .then(function (items) {
                barcodeIndex = items;
                barcodeIndexLoadedFromCache = false;
                writeBarcodeCache(items);
                return items;
            })
            .finally(function () {
                barcodeIndexPromise = null;
            });

        return barcodeIndexPromise;
    }

    async function fetchCompleteBarcodeIndex() {
        const items = Object.create(null);
        let hasNextPage = true;
        let endCursor = null;

        while (hasNextPage) {
            const body = {
                query: `query BarcodeIndex($cursor: String, $country: CountryCode!) @inContext(country: $country) {
                    products(first: 250, after: $cursor) {
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                        edges {
                            node {
                                metafield(namespace: "masterdata", key: "Eancode") {
                                    value
                                }
                                variants(first: 100) {
                                    edges {
                                        node {
                                            id
                                            sku
                                            barcode
                                        }
                                    }
                                }
                            }
                        }
                    }
                }`,
                variables: {
                    cursor: endCursor,
                    country: STOREFRONT_COUNTRY_CODE
                }
            };

            const response = await fetch(GRAPHQL_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': STOREFRONT_API_TOKEN
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error('Shopify Barcode-Index: HTTP ' + response.status);
            }

            const data = await response.json();
            if (data.errors && data.errors.length) {
                throw new Error(data.errors[0].message || 'Shopify Barcode-Index konnte nicht geladen werden.');
            }

            const products = data && data.data && data.data.products;
            if (!products) {
                throw new Error('Shopify Barcode-Index: ungültige Antwort.');
            }

            products.edges.forEach(function (edge) {
                const node = edge.node;
                const variants = node.variants && node.variants.edges
                    ? node.variants.edges.map(function (variantEdge) { return variantEdge.node; })
                    : [];

                // 1) Das bisher verwendete Produkt-Metafeld bleibt vollständig erhalten.
                // Es wird wie bisher der ersten Variante zugeordnet.
                const metafieldBarcode = node.metafield && node.metafield.value;
                if (metafieldBarcode && variants[0]) {
                    addBarcodeMapping(items, metafieldBarcode, variants[0]);
                }

                // 2) Zusätzlich werden die nativen Shopify-Barcodes aller geladenen
                // Varianten berücksichtigt. So funktioniert der Scan auch dann, wenn
                // die EAN nur am Varianten-Barcode gepflegt ist.
                variants.forEach(function (variant) {
                    if (variant && variant.barcode) {
                        addBarcodeMapping(items, variant.barcode, variant);
                    }
                });
            });

            hasNextPage = products.pageInfo.hasNextPage;
            endCursor = products.pageInfo.endCursor;
        }

        return items;
    }

    async function getProductFromBarcode(barcode) {
        const candidates = barcodeCandidates(barcode);
        if (!candidates.length) return null;

        const index = await loadBarcodeIndex(false);
        let match = null;

        candidates.some(function (candidate) {
            if (index && index[candidate]) {
                match = index[candidate];
                return true;
            }
            return false;
        });

        // Falls ein neues Produkt seit Erstellung des 24h-Caches hinzugekommen ist,
        // aktualisieren wir bei einem Fehltreffer einmal sofort.
        if (!match && barcodeIndexLoadedFromCache) {
            const refreshedIndex = await loadBarcodeIndex(true);
            candidates.some(function (candidate) {
                if (refreshedIndex && refreshedIndex[candidate]) {
                    match = refreshedIndex[candidate];
                    return true;
                }
                return false;
            });
        }

        if (!match) return null;
        return {
            sku: match.s,
            variantId: match.v
        };
    }

    async function processImageForIOS(file) {
        let imageFile = file;

        if (file.type.match(/heic|heif/i) || file.name.match(/\.hei[cf]$/i)) {
            imageFile = await convertHeicToJpeg(file);
        }

        return resizeImageForUpload(imageFile);
    }

    async function resizeImageForUpload(file) {
        if (!file || !String(file.type || '').match(/^image\//i)) return file;

        let objectUrl;
        try {
            objectUrl = URL.createObjectURL(file);
            const img = await loadImage(objectUrl);
            const width = img.naturalWidth || img.width;
            const height = img.naturalHeight || img.height;

            if (!width || !height) return file;

            const largestSide = Math.max(width, height);
            const scale = Math.min(1, IMAGE_MAX_DIMENSION / largestSide);

            // Kleine Bilder müssen nicht neu kodiert werden.
            if (scale === 1 && file.size <= 1200 * 1024 && /image\/jpe?g/i.test(file.type)) {
                return file;
            }

            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(width * scale));
            canvas.height = Math.max(1, Math.round(height * scale));

            const context = canvas.getContext('2d');
            if (!context) return file;

            context.drawImage(img, 0, 0, canvas.width, canvas.height);

            const blob = await canvasToBlob(canvas, 'image/jpeg', IMAGE_JPEG_QUALITY);
            if (!blob) return file;

            // Keine Verschlechterung, wenn das Original ohnehin kleiner ist.
            if (scale === 1 && blob.size >= file.size) return file;

            const baseName = (file.name || 'barcode').replace(/\.[^/.]+$/, '');
            return new File([blob], baseName + '.jpg', {
                type: 'image/jpeg',
                lastModified: Date.now()
            });
        } catch (error) {
            console.warn('Bildoptimierung übersprungen:', error);
            return file;
        } finally {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        }
    }

    function loadImage(src) {
        return new Promise(function (resolve, reject) {
            const img = new Image();
            img.onload = function () { resolve(img); };
            img.onerror = reject;
            img.src = src;
        });
    }

    function canvasToBlob(canvas, type, quality) {
        return new Promise(function (resolve) {
            canvas.toBlob(resolve, type, quality);
        });
    }

    async function scanBarcodeFromImage(file) {
        const addButton = document.getElementById('addToCartSkuBtn');
        const handleInput = document.getElementById('handle');
        if (!addButton || !handleInput) return;

        const originalButtonText = addButton.textContent;
        const formData = new FormData();
        formData.append('imageFile', file);

        addButton.disabled = true;
        addButton.textContent = '⏳';

        try {
            const response = await fetch('https://api.cloudmersive.com/barcode/scan/image', {
                method: 'POST',
                headers: { 'Apikey': CLOUDMERSIVE_API_KEY },
                body: formData
            });

            if (!response.ok) throw new Error('API-Fehler: ' + response.status);

            const data = await response.json();
            const barcode = normalizeBarcode(data && data.RawText);

            if (!barcode) {
                alert('Kein Barcode gefunden! Bitte anderes Foto versuchen.');
                return;
            }

            const product = await getProductFromBarcode(barcode);
            if (!product) {
                alert('Kein Produkt mit diesem Barcode gefunden!');
                return;
            }

            handleInput.value = product.sku;
            handleInput.dataset.scannerSku = product.sku;
            handleInput.dataset.scannerVariantId = product.variantId;

            // Für andere Theme-Skripte optional verfügbar.
            window.WusthofLastScannedProduct = product;
        } catch (error) {
            console.error('Scan-Fehler:', error);
            alert('Scanner-Fehler: ' + error.message);
        } finally {
            addButton.disabled = false;
            addButton.textContent = originalButtonText;
        }
    }

    async function convertHeicToJpeg(file) {
        if (!window.HEIC2JPG) {
            // Mobile Safari liefert beim Kamera-Upload üblicherweise bereits ein
            // browserlesbares Bild. Falls nicht, fällt resizeImageForUpload sauber
            // auf das Original zurück.
            return file;
        }

        try {
            const jpegBlob = await window.HEIC2JPG(file);
            return new File([jpegBlob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                type: 'image/jpeg'
            });
        } catch (error) {
            console.warn('HEIC-Konvertierung fehlgeschlagen:', error);
            return file;
        }
    }
})();
